// Session storage for the admin web app.
//
// The platform is passwordless: sign-in issues an ES256 access+refresh token
// pair (passkey / Sign in with Apple / email magic link). We persist the whole
// pair so the API client can silently refresh the short-lived access token.
//
// A one-time bootstrap path (pasting an admin access token) is kept so the very
// first admin can sign in and enrol a passkey — enrolling a passkey requires
// already being authenticated. Pasted tokens have no refresh token; once the
// access token expires the user re-pastes or (better) uses their new passkey.

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  savedAt: number;
}

const KEY = 'checkid_admin_session';

export function getSession(): StoredSession | null {
  const raw = localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function setSession(pair: {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}): void {
  localStorage.setItem(
    KEY,
    JSON.stringify({ ...pair, savedAt: Date.now() } satisfies StoredSession),
  );
}

/** Bootstrap path: a pasted access token with no refresh token. */
export function setBootstrapToken(accessToken: string): void {
  setSession({ accessToken: accessToken.trim(), refreshToken: '', expiresIn: 900 });
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

// Sign-in hints, kept ACROSS sign-out so the next sign-in is one tap: the last
// email used (to prefill) and whether a passkey has been used on this browser
// (to lead with it automatically).
const LAST_EMAIL = 'checkid_admin_lastEmail';
const PREFER_PASSKEY = 'checkid_admin_preferPasskey';

export function getLastEmail(): string | null {
  return localStorage.getItem(LAST_EMAIL);
}
export function rememberEmail(email: string): void {
  localStorage.setItem(LAST_EMAIL, email);
}
export function prefersPasskey(): boolean {
  return localStorage.getItem(PREFER_PASSKEY) === '1';
}
export function markPasskeyUsed(): void {
  localStorage.setItem(PREFER_PASSKEY, '1');
}

export function getToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  const t = getSession()?.refreshToken;
  return t ? t : null;
}

export function isAuthed(): boolean {
  return !!getToken();
}
